import { useCallback, useEffect, useState } from 'react';

import { getMySubscription, getPaymentHistory } from '../api/subscription.api';
import type { PaymentRecord, UserSubscription } from '../api/subscription.api';
import { useAuth } from '../context/AuthContext';

/**
 * The signed-in user's subscription and payment history.
 *
 * Replaces `useDashboardData`, which also carried question history read out of
 * localStorage and a fabricated single-entry leaderboard. Both belonged to the
 * question generator and neither survives the pivot.
 *
 * Deliberately not routed through `RequestCache`: this is one small request pair
 * per session, it must reflect a just-completed payment immediately, and
 * `frontend-caching.md` is explicit that caching is for repeated reads of stable
 * data rather than for everything that happens to be fetched.
 */
export function useSubscriptionData() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by refresh() to re-run the effect without duplicating the fetch logic.
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setPayments([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const [sub, history] = await Promise.all([getMySubscription(), getPaymentHistory()]);
        if (cancelled) return;
        setSubscription(sub);
        setPayments(history);
      } catch (err: unknown) {
        if (cancelled) return;
        // A 401 or a ban never reaches here — apiFetch converts both into the
        // session-expired / account-suspended flows before this sees them.
        setError((err as { message?: string }).message ?? 'Failed to load subscription');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  return { subscription, payments, loading, error, refresh };
}
